import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import { sanitizeEmailInput } from "utils/inputFilters";
import { transformPropertiesToLocal } from "utils/propertyTransforms";
import { separateAndFormatTenants } from "utils/tenantUtils";
import { removeAccents } from "utils/utils";
import {
  validateEmailNotAdvisor,
  validatePhoneNotAdvisor,
  validatePhoneNumber,
} from "utils/validators/generalValidators";

import { useCreateTenantReport } from "hooks/useCreateTenantReport";
import { useGetProperties } from "hooks/useGetProperties";
import { useGetTenants } from "hooks/useGetTenants";
import usePageLoading from "hooks/usePageLoading";

import {
  PageLoadingContainer,
  SkeletonBadge,
  SkeletonBlock,
  SkeletonHeading,
  SkeletonText,
  SlowStateBanner,
  StepsSkeleton,
} from "js/components/Loading";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "js/components/ui/alert-dialog";
import { Badge } from "js/components/ui/badge";
import { Button } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import { StepIndicator } from "js/components/ui/step-indicator";

import { VALID_PROPERTY_USE_VALUES } from "js/constants/property-use";
import { getTenantReportErrorMessage } from "js/constants/tenant-report-errors";

import { useAppSelector } from "../../../redux/hooks";
import { toast } from "../../../utils/toast";
import { formatCurrency } from "../../../utils/utils";
import {
  PropertyRegistration,
  PropertyRegistrationRef,
} from "./components/PropertyRegistration";
import { Step1PropertySelection } from "./components/Step1PropertySelection";
import { Step2TenantSelection } from "./components/Step2TenantSelection";
import { Step3GuarantorConfiguration } from "./components/Step3GuarantorConfiguration";
import { Step4FinalConfiguration } from "./components/Step4FinalConfiguration";
import { Step5ReviewAndOffers } from "./components/Step5ReviewAndOffers";
import { TenantRegistration } from "./components/TenantRegistration";
import { PROPERTY_REGISTRATION_STEPS, STEPS } from "./constants";
import { useModalViewTransition } from "./hooks/useModalViewTransition";
import { useRentalApplicationForm } from "./hooks/useRentalApplicationForm";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { FormData, RentalApplicationModalProps } from "./types";
import {
  isGuarantorConfigurationValid,
  isLegalProtectionStepValid,
  isPropertySelectionValid,
  isTenantSelectionValid,
} from "./validation";

const RentalApplicationModal = ({
  isOpen,
  onOpenChange,
  propertyId,
}: RentalApplicationModalProps) => {
  const navigate = useNavigate();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // When navigating from Step 5 recommendation to Step 3, temporarily
  // highlight the credit investigation box to orient the user.
  const [highlightGuarantorCredit, setHighlightGuarantorCredit] =
    useState(false);
  const [showGuarantorVerificationDialog, setShowGuarantorVerificationDialog] =
    useState(false);
  const [guarantorEmailError, setGuarantorEmailError] = useState<string>("");
  const [guarantorPhoneError, setGuarantorPhoneError] = useState<string>("");

  const { userData } = useAppSelector((state) => state.entities.user);

  const {
    formData,
    updateFormData,
    updateMultipleFormData,
    resetForm,
    properties,
    setProperties,
    selectedProperty,
    newTenant,
    setNewTenant,
    tenants,
    resetNewTenant,
  } = useRentalApplicationForm();

  const { mutateAsync: createReport, isPending: isCreating } =
    useCreateTenantReport();

  const {
    data: fetchedProperties,
    isLoading: propertiesLoading,
    refetch: refetchProperties,
    error: propertiesError,
  } = useGetProperties();
  const {
    data: fetchedTenants,
    isLoading: tenantsLoading,
    refetch: refetchTenants,
    error: tenantsError,
  } = useGetTenants();

  // Process tenants data using utility function
  const { naturalPersons, legalPersons } = fetchedTenants
    ? separateAndFormatTenants(fetchedTenants)
    : { naturalPersons: [], legalPersons: [] };

  const hasFetchedData = Boolean(fetchedProperties && fetchedTenants);
  const hasError = Boolean(propertiesError || tenantsError);

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading:
      propertiesLoading || tenantsLoading || (!hasFetchedData && !hasError),
    enabled: isOpen,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void Promise.all([refetchProperties(), refetchTenants()]);
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <Button
          size="sm"
          variant="outline"
          className="border-amber-300 bg-amber-50/70 text-amber-700 hover:bg-amber-50"
          onClick={handleRetryFetch}
        >
          Reintentar
        </Button>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-4">
      <SkeletonHeading className="h-7 w-72" />
      <StepsSkeleton steps={5} />
    </div>
  );

  const actionsSkeleton = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <SkeletonBlock className="h-11 w-32 rounded-md" />
      <div className="flex items-center gap-3">
        <SkeletonBadge className="h-6 w-20" />
        <SkeletonBlock className="h-11 w-32 rounded-md" />
      </div>
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-6">
      <div className="space-y-4">
        <SkeletonHeading className="h-6 w-48" />
        <SkeletonText className="w-full" />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <SkeletonText className="h-4 w-32" />
          <SkeletonBlock className="h-10 rounded-md" />
        </div>
        <div className="space-y-2">
          <SkeletonText className="h-4 w-32" />
          <SkeletonBlock className="h-10 rounded-md" />
        </div>
        <div className="space-y-2">
          <SkeletonText className="h-4 w-32" />
          <SkeletonBlock className="h-10 rounded-md" />
        </div>
        <div className="space-y-2">
          <SkeletonText className="h-4 w-32" />
          <SkeletonBlock className="h-10 rounded-md" />
        </div>
      </div>
      <div className="space-y-3 rounded-lg border border-slate-200/70 bg-slate-50/50 p-4">
        <SkeletonText className="h-5 w-40" />
        <SkeletonBlock className="h-10 w-full rounded-md" />
      </div>
    </div>
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  const {
    currentStep,
    isAnimating,
    handleNext: originalHandleNext,
    handleBack: originalHandleBack,
    handleStepClick: originalHandleStepClick,
    getStepProgress,
    resetStep,
  } = useStepNavigation();

  // Wrap navigation functions to include scroll to top
  const handleNext = async () => {
    // Validate guarantor email and phone on step 3 if credit investigation is enabled
    if (
      currentStep === 3 &&
      formData.requiresGuarantor &&
      formData.creditInvestigation
    ) {
      // Validate email
      if (sanitizeEmailInput(formData.guarantorEmail)) {
        try {
          await validateEmailNotAdvisor(
            userData,
            sanitizeEmailInput(formData.guarantorEmail),
          );
          setGuarantorEmailError(""); // Clear error if validation passes
        } catch (error: any) {
          const errorMessage = error.message || "Email del fiador inválido";
          setGuarantorEmailError(errorMessage);
          toast.error(errorMessage);
          return;
        }
      }

      // Validate phone number
      if (formData.guarantorPhoneNumber?.trim()) {
        try {
          await validatePhoneNumber(null, formData.guarantorPhoneNumber.trim());
          await validatePhoneNotAdvisor(
            userData,
            formData.guarantorPhoneNumber.trim(),
          );
          setGuarantorPhoneError(""); // Clear error if validation passes
        } catch (error: any) {
          const errorMessage = error.message || "Teléfono del fiador inválido";
          setGuarantorPhoneError(errorMessage);
          toast.error(errorMessage);
          return;
        }
      }
    }

    if (
      currentStep === 3 &&
      formData.requiresGuarantor &&
      !formData.creditInvestigation
    ) {
      setShowGuarantorVerificationDialog(true);
      return;
    }

    originalHandleNext();
    setTimeout(scrollToTop, 50); // Small delay to ensure DOM update
  };

  const handleSkipGuarantorVerification = () => {
    setShowGuarantorVerificationDialog(false);
    originalHandleNext();
    setTimeout(scrollToTop, 50);
  };

  const handleEnableGuarantorVerification = () => {
    setShowGuarantorVerificationDialog(false);
    updateFormData("creditInvestigation", true);
    setHighlightGuarantorCredit(true);
  };

  const handleBack = () => {
    originalHandleBack();
    setTimeout(scrollToTop, 50);
  };

  const handleStepClick = (stepNumber: number) => {
    originalHandleStepClick(stepNumber);
    setTimeout(scrollToTop, 50);
  };

  const {
    modalView,
    viewTransition,
    previousView,
    propertySheetStep,
    setPropertySheetStep,
    tenantSheetStep,
    setTenantSheetStep,
    transitionToPropertyRegistration,
    transitionToTenantRegistration,
    transitionBackToRentalApplication,
    resetView,
  } = useModalViewTransition();

  // Ref to access PropertyRegistration methods
  const propertyRegistrationRef = useRef<PropertyRegistrationRef>(null);

  // Check if there's content to scroll and update indicator visibility
  const checkScrollable = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      const hasScrollableContent = scrollHeight > clientHeight;
      // More precise check - consider it at bottom if within 5px of the end
      const isNotAtBottom = scrollTop + clientHeight < scrollHeight - 5;
      setShowScrollIndicator(hasScrollableContent && isNotAtBottom);
    }
  };

  // Scroll to bottom when indicator is clicked
  const scrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // Set up scroll listener and MutationObserver
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const handleScroll = () => {
        checkScrollable();
      };

      scrollContainer.addEventListener("scroll", handleScroll);

      // Set up MutationObserver to detect content changes
      const observer = new MutationObserver(() => {
        checkScrollable();
      });

      observer.observe(scrollContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });

      // Check initially after a small delay to ensure content is rendered
      setTimeout(checkScrollable, 100);

      // Also check when window resizes
      const handleResize = () => checkScrollable();
      window.addEventListener("resize", handleResize);

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
        observer.disconnect();
      };
    }
  }, [modalView, currentStep, propertySheetStep, tenantSheetStep]);

  // Re-check scrollable content when step changes or modal opens
  useEffect(() => {
    setTimeout(checkScrollable, 100);
    // Also check after animations complete
    setTimeout(checkScrollable, 500);
  }, [currentStep, propertySheetStep, tenantSheetStep, modalView, isOpen]);

  // Check when form data changes (especially property selection)
  useEffect(() => {
    checkScrollable();
    setTimeout(checkScrollable, 100);
  }, [
    formData.property,
    formData.tenant,
    formData.requiresGuarantor,
    formData.creditInvestigation,
  ]);

  // Update properties when fetched data is available
  useEffect(() => {
    if (fetchedProperties) {
      const transformedProperties =
        transformPropertiesToLocal(fetchedProperties);
      setProperties(transformedProperties);
    }
  }, [fetchedProperties, setProperties]);

  // Preselect property when modal opens with propertyId
  useEffect(() => {
    if (isOpen && propertyId && properties.length > 0) {
      const property = properties.find((p) => p.id === propertyId);
      if (property && formData.property !== propertyId) {
        updateFormData("property", propertyId);
        // Auto-populate fields when a property is selected
        // Only set propertyUse if it's a valid value
        if (property.uso && VALID_PROPERTY_USE_VALUES.includes(property.uso)) {
          updateFormData("propertyUse", property.uso);
        } else {
          // Clear invalid property use value
          updateFormData("propertyUse", "");
        }
        if (property.rental_price !== undefined) {
          updateFormData("rentAmount", String(property.rental_price));
        }
        // Always update maintenance amount, even if 0
        if (property.maintenance_price !== undefined) {
          updateFormData(
            "maintenanceAmount",
            String(property.maintenance_price),
          );
        } else {
          // Clear the field if maintenance_price is not defined
          updateFormData("maintenanceAmount", "");
        }
      }
    }
  }, [isOpen, propertyId, properties, formData.property, updateFormData]);

  // Clear highlight after 2 seconds when triggered
  useEffect(() => {
    if (highlightGuarantorCredit) {
      const timer = setTimeout(() => setHighlightGuarantorCredit(false), 2700);
      return () => clearTimeout(timer);
    }
  }, [highlightGuarantorCredit]);

  // Step configuration
  const steps = STEPS.map((step) => ({
    ...step,
    completed: currentStep > step.number,
  }));

  // Property registration step configuration
  const propertySteps = PROPERTY_REGISTRATION_STEPS.map((step) => ({
    ...step,
    completed: propertySheetStep > step.number,
  }));

  const getPropertyStepProgress = () => {
    if (propertySheetStep === 1) return 0;
    if (propertySheetStep === 2) return 100;
    return 0;
  };

  const handleSubmit = async () => {
    // Build payload to match CreateReport.tsx onFinish
    try {
      setIsSubmitting(true);

      // Validate guarantor email if provided
      if (
        formData.requiresGuarantor &&
        formData.creditInvestigation &&
        sanitizeEmailInput(formData.guarantorEmail)
      ) {
        try {
          await validateEmailNotAdvisor(
            userData,
            sanitizeEmailInput(formData.guarantorEmail),
          );
        } catch (error: any) {
          toast.error(error.message || "Email del fiador inválido");
          setIsSubmitting(false);
          return;
        }
      }

      const payload: any = {
        person: formData.tenant,
        property: formData.property,
        rental_price: formData.rentAmount
          ? parseFloat(formData.rentAmount)
          : 0,
        maintenance_price: formData.maintenanceAmount
          ? parseFloat(formData.maintenanceAmount)
          : 0,
        property_usage: formData.propertyUse,
        guarantor_required: formData.requiresGuarantor,
        guarantor_investigation_required: formData.creditInvestigation,
        guarantor_proof_of_income_required: formData.guarantorProofOfIncome,
        property_as_collateral_required: formData.propertyAsGuarantee,
        alternative_justice_agreement_required: Boolean(
          formData.alternativeJustice,
        ),
        secure_rent_policy_required: Boolean(formData.rentInsurance),
        tenant_makes_payment: Boolean(formData.tenantPaysApplication),
        web_check_required: formData.webCheckRequired,
        legal_type: formData.tenantType === "moral" ? "2" : "1",
      };

      if (formData.requiresGuarantor) {
        let guarantor: Record<string, any> = {
          legal_type: formData.guarantorType === "moral" ? "2" : "1",
        };

        if (formData.creditInvestigation) {
          guarantor = {
            legal_type: formData.guarantorType === "moral" ? "2" : "1",
            name: formData.guarantorName?.trim()
              ? removeAccents(formData.guarantorName.trim())
              : undefined,
            first_surname: formData.guarantorFirstSurname?.trim()
              ? removeAccents(formData.guarantorFirstSurname.trim())
              : undefined,
            second_surname: formData.guarantorSecondSurname?.trim()
              ? removeAccents(formData.guarantorSecondSurname.trim())
              : undefined,
            email: sanitizeEmailInput(formData.guarantorEmail),
            phone_number: formData.guarantorPhoneNumber?.trim(),
            dial_code: formData.guarantorDialCode?.trim(),
          };
        }

        Object.keys(guarantor).forEach((key) => {
          if (!guarantor[key]) delete guarantor[key];
        });
        payload.guarantor = guarantor;
      }

      const loadingToastId = toast.loading("Creando...");
      try {
        const result = await createReport(payload);
        toast.dismiss(loadingToastId);
        // On success: close dialog and clear state
        onOpenChange(false);
        resetStep();
        resetForm();
        resetView();
        setIsSubmitting(false);

        if (result?.id) {
          navigate(`/tenants/reports/${result.id}`);
        }
      } catch (err: unknown) {
        toast.dismiss(loadingToastId);
        const errorMessage = getTenantReportErrorMessage(err);
        toast.error(errorMessage);
        setIsSubmitting(false);
      }
    } catch (e) {
      setIsSubmitting(false);
      toast.error("Ocurrió un error inesperado.");
    }
  };

  const handleTenantCreated = (newTenantData: any) => {
    // Automatically select the newly created tenant
    if (newTenantData?.id) {
      // Determine tenant type - convert to string first to handle both number and string types
      const tenantType =
        String(newTenantData.legal_type) === "2" ? "moral" : "fisica";

      // Populate tenant information fields - always show representative's name, not legal entity name
      const fullName =
        `${newTenantData.name || ""} ${newTenantData.first_surname || ""} ${newTenantData.second_surname || ""}`.trim();

      // Update ALL fields in a SINGLE setState operation to avoid timing issues
      updateMultipleFormData({
        tenantType,
        tenant: newTenantData.id,
        tenantName: fullName,
        tenantPhone: `${newTenantData.dial_code || "+52"} ${newTenantData.phone_number || ""}`,
        tenantEmail: newTenantData.email || "",
      });
    }

    setTenantSheetStep(1);
    resetNewTenant();

    setTimeout(() => {
      transitionBackToRentalApplication();
    }, 0);
  };

  const handleTenantError = (errorMessage: string) => {
    // Could integrate with a toast system here
    alert(errorMessage);
  };

  // Custom cancel handler that will be passed to PropertyRegistration
  const handlePropertyRegistrationCancel = () => {
    transitionBackToRentalApplication();
  };

  // Handler for when a property is successfully created
  const handlePropertyCreated = (newProperty: any) => {
    // Transform the new property to local format
    const transformedProperty = transformPropertiesToLocal([newProperty])[0];

    // Update the properties list with the new property
    setProperties((prevProperties) => [transformedProperty, ...prevProperties]);

    // Automatically select the newly created property
    if (newProperty.id) {
      const updates: Partial<FormData> = {
        property: newProperty.id,
      };

      // Auto-populate fields with the new property data
      // Only set propertyUse if it's a valid value
      if (
        transformedProperty.uso &&
        VALID_PROPERTY_USE_VALUES.includes(transformedProperty.uso)
      ) {
        updates.propertyUse = transformedProperty.uso;
      } else {
        // Clear invalid property use value
        updates.propertyUse = "";
      }
      if (newProperty.rental_price !== undefined) {
        updates.rentAmount = String(newProperty.rental_price);
      }
      // Always update maintenance amount, even if 0
      if (newProperty.maintenance_price !== undefined) {
        updates.maintenanceAmount = String(newProperty.maintenance_price);
      } else {
        // Clear the field if maintenance_price is not defined
        updates.maintenanceAmount = "";
      }

      updateMultipleFormData(updates);
    }
  };

  // Validation is now handled by the TenantRegistration component
  // via the validateForm callback that updates isTenantFormValid

  // Check if user has entered any data
  const hasUserEnteredData = () => {
    // Check if any form field has been filled
    return (
      formData.property !== "" ||
      formData.propertyUse !== "" ||
      formData.rentAmount !== "" ||
      formData.maintenanceAmount !== "" ||
      formData.tenantName !== "" ||
      formData.tenantPhone !== "" ||
      formData.tenantEmail !== "" ||
      formData.tenant !== "" ||
      formData.requiresGuarantor ||
      formData.creditInvestigation ||
      formData.alternativeJustice !== null ||
      formData.rentInsurance !== null ||
      formData.tenantPaysApplication !== null ||
      typeof formData.webCheckRequired === "boolean" ||
      currentStep > 1
    );
  };

  const handleCancelClick = () => {
    if (hasUserEnteredData()) {
      setShowCancelConfirmation(true);
    } else {
      onOpenChange(false);
      resetStep();
      resetForm();
      resetView();
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirmation(false);
    onOpenChange(false);
    resetStep();
    resetForm();
    resetView();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      // User clicked X or pressed escape - treat it like cancel
      handleCancelClick();
    } else {
      onOpenChange(open);
    }
  };

  // Compute label for guarantor verification CTA based on guarantor type
  const guarantorVerificationPrice =
    formData.guarantorType === "moral" ? 499 : 299;
  const computedGuarantorVerificationLabel = `Añadir Verificación por ${formatCurrency(guarantorVerificationPrice)}`;

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="w-full max-w-[calc(100vw-2rem)] sm:max-w-4xl sm:min-w-[56rem] max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-none sm:rounded-lg"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {!showSkeleton ? (
          <>
            <DialogHeader className="pb-4 shrink-0">
              <DialogTitle className="text-lg sm:text-xl font-semibold text-gray-900">
                {modalView === "rental-application"
                  ? "Crear Solicitud de Renta"
                  : modalView === "property-registration"
                    ? "Registrar Inmueble"
                    : "Registrar Inquilino"}
              </DialogTitle>
              {modalView === "rental-application" && (
                <StepIndicator
                  steps={steps}
                  currentStep={currentStep}
                  onStepClick={handleStepClick}
                  getStepProgress={getStepProgress}
                />
              )}
              {modalView === "property-registration" && (
                <StepIndicator
                  steps={propertySteps}
                  currentStep={propertySheetStep}
                  onStepClick={(stepNumber) => {
                    if (stepNumber <= propertySheetStep) {
                      setPropertySheetStep(stepNumber);
                    }
                  }}
                  getStepProgress={getPropertyStepProgress}
                />
              )}
            </DialogHeader>
            <div
              ref={scrollContainerRef}
              className="relative flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
            >
              {showScrollIndicator && (
                <div className="fixed bottom-24 right-4 z-50">
                  <button
                    onClick={scrollToBottom}
                    className="bg-gray-500/80 backdrop-blur text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-1 hover:bg-gray-600/80 transition-colors"
                  >
                    <span className="text-xs font-medium">Ver más</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              )}

              {(modalView === "rental-application" ||
                previousView === "rental-application") && (
                <div
                  className={`${previousView === "rental-application" ? "absolute inset-0" : ""} transition-all duration-400 transform ${
                    modalView === "rental-application"
                      ? viewTransition
                        ? "translate-x-10 opacity-0"
                        : "translate-x-0 opacity-100"
                      : "translate-x-0 opacity-0 pointer-events-none"
                  }`}
                >
                  <div className="min-h-[400px]">
                    {currentStep === 1 && (
                      <Step1PropertySelection
                        formData={formData}
                        updateFormData={updateFormData}
                        isAnimating={isAnimating}
                        properties={properties}
                        selectedProperty={selectedProperty}
                        transitionToPropertyRegistration={
                          transitionToPropertyRegistration
                        }
                      />
                    )}
                    {currentStep === 2 && (
                      <Step2TenantSelection
                        formData={formData}
                        updateFormData={updateFormData}
                        isAnimating={isAnimating}
                        tenantsDirectory={tenants}
                        naturalPersons={naturalPersons}
                        legalPersons={legalPersons}
                        tenantsLoading={tenantsLoading}
                        tenantsError={tenantsError}
                        transitionToTenantRegistration={
                          transitionToTenantRegistration
                        }
                      />
                    )}
                    {currentStep === 3 && (
                      <Step3GuarantorConfiguration
                        formData={formData}
                        updateFormData={updateFormData}
                        isAnimating={isAnimating}
                        highlightGuarantorCredit={highlightGuarantorCredit}
                        externalEmailError={guarantorEmailError}
                        clearExternalEmailError={() =>
                          setGuarantorEmailError("")
                        }
                        externalPhoneError={guarantorPhoneError}
                        clearExternalPhoneError={() =>
                          setGuarantorPhoneError("")
                        }
                      />
                    )}
                    {currentStep === 4 && (
                      <Step4FinalConfiguration
                        formData={formData}
                        updateFormData={updateFormData}
                        isAnimating={isAnimating}
                      />
                    )}
                    {currentStep === 5 && (
                      <Step5ReviewAndOffers
                        formData={formData}
                        updateFormData={updateFormData}
                        isAnimating={isAnimating}
                        properties={properties}
                        onNavigateToStep={(step) => {
                          if (step === 3) {
                            setHighlightGuarantorCredit(true);
                          }
                          handleStepClick(step);
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {(modalView === "property-registration" ||
                previousView === "property-registration") && (
                <div
                  className={`${previousView === "property-registration" ? "absolute inset-0" : ""} transition-all duration-400 transform ${
                    modalView === "property-registration"
                      ? viewTransition
                        ? "translate-x-10 opacity-0"
                        : "translate-x-0 opacity-100"
                      : "translate-x-0 opacity-0 pointer-events-none"
                  }`}
                >
                  <PropertyRegistration
                    ref={propertyRegistrationRef}
                    propertySheetStep={propertySheetStep}
                    setPropertySheetStep={setPropertySheetStep}
                    onCancelClick={handlePropertyRegistrationCancel}
                    onPropertyCreated={handlePropertyCreated}
                    isVisible={modalView === "property-registration"}
                  />
                </div>
              )}

              {(modalView === "tenant-registration" ||
                previousView === "tenant-registration") && (
                <div
                  className={`${previousView === "tenant-registration" ? "absolute inset-0" : ""} transition-all duration-400 transform ${
                    modalView === "tenant-registration"
                      ? viewTransition
                        ? "translate-x-10 opacity-0"
                        : "translate-x-0 opacity-100"
                      : "translate-x-0 opacity-0 pointer-events-none"
                  }`}
                >
                  <TenantRegistration
                    tenantSheetStep={tenantSheetStep}
                    newTenant={newTenant}
                    setNewTenant={setNewTenant}
                    tenantType={formData.tenantType}
                    onTenantCreated={handleTenantCreated}
                    onError={handleTenantError}
                    onCancel={transitionBackToRentalApplication}
                  />
                </div>
              )}
            </div>

            {/* Footer for rental application modal */}
            {modalView === "rental-application" && (
              <DialogFooter className="flex-row items-center justify-between pt-4 border-t border-gray-200 shrink-0 gap-2 sm:justify-between">
                {currentStep === 1 ? (
                  <Button
                    variant="ghost"
                    onClick={handleCancelClick}
                    className="h-11 px-3 sm:px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={handleBack}
                    className="h-11 px-3 sm:px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
                    Regresar
                  </Button>
                )}
                <div className="flex items-center gap-2 sm:gap-4">
                  <Badge
                    variant="secondary"
                    className="px-2 py-1 bg-gray-100 text-gray-700 text-[11px] sm:px-2.5 sm:text-xs"
                  >
                    <span className="sm:hidden">{currentStep}/5</span>
                    <span className="hidden sm:inline">
                      Paso {currentStep} de 5
                    </span>
                  </Badge>
                  {currentStep < 5 ? (
                    <Button
                      onClick={handleNext}
                      disabled={
                        (currentStep === 1 &&
                          !isPropertySelectionValid(formData)) ||
                        (currentStep === 2 &&
                          !isTenantSelectionValid(formData)) ||
                        (currentStep === 3 &&
                          !isGuarantorConfigurationValid(formData)) ||
                        (currentStep === 4 &&
                          !isLegalProtectionStepValid(formData))
                      }
                      className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white h-11 px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                      <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting || isCreating}
                      className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white h-11 px-4 sm:px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting || isCreating
                        ? "Guardando..."
                        : "Crear Solicitud"}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            )}
          </>
        ) : (
          <PageLoadingContainer
            showSkeleton={showSkeleton}
            slowState={slowStateBanner}
            headerSkeleton={headerSkeleton}
            actionsSkeleton={actionsSkeleton}
            contentSkeleton={contentSkeleton}
            className="flex flex-col"
            ariaLabel="Cargando solicitud de renta..."
          />
        )}
      </DialogContent>

      <AlertDialog
        open={showGuarantorVerificationDialog}
        onOpenChange={setShowGuarantorVerificationDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Blinda tu Garantía al 100%</AlertDialogTitle>
            <AlertDialogDescription>
              Vemos que solicitaste un fiador pero no su verificación. Obtén un
              reporte Premium que incluye antecedentes legales, buró de crédito,
              listas negras y Web Check global del fiador.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleSkipGuarantorVerification}
              className="px-3 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-transparent border-0 shadow-none"
            >
              Continuar sin verificación
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEnableGuarantorVerification}
              className="bg-[#0f5ef7] hover:bg-[#0d52e0]"
            >
              {computedGuarantorVerificationLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showCancelConfirmation}
        onOpenChange={setShowCancelConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Estás seguro que deseas cancelar?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Has ingresado información en el formulario. Si cancelas ahora,
              perderás todos los datos ingresados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>
              Sí, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default RentalApplicationModal;
